import { Box, IconButton, ListItem, Stack, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import CloseIconButton from "@entrilia/shared/components/CloseIconButton";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis.tsx";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper.ts";
import type { SortField } from "../../../../../../../../api/types/svTypes.ts";
import { Ordering } from "../../../../../../../../api/types/svTypes.ts";
import SortAscIcon from "@entrilia/shared/icons/SortAscIcon";
import SortDescIcon from "@entrilia/shared/icons/SortDescIcon";

interface Props {
  field: SortField;
  onRemoveItem: () => void;
  onChangeSorting: (order: Ordering) => void;
  filterMap?: Array<[string, string]>;
  readonlyMode?: boolean;
}

const FieldNameCaptionPart = ({ field, readonlyMode }: { field: SortField; readonlyMode?: boolean }) => {
  return (
    <TypographyTooltipEllipsis
      text={field.config.caption ?? field.meta.caption}
      typographyProps={{
        variant: "caption",
        noWrap: true,
        sx: { color: readonlyMode ? "text.disabled" : "text.primary" },
      }}
      tooltipProps={{ disableInteractive: true }}
    />
  );
};

export const SortFieldItem = ({ field, onRemoveItem, onChangeSorting, filterMap = [], readonlyMode }: Props) => {
  const Caption = useMemo(() => {
    if (filterMap.length > 0) {
      return (
        <>
          <FieldNameCaptionPart field={field} readonlyMode={readonlyMode} />
          {filterMap.length > 0 && (
            <TypographyTooltipEllipsis
              text={filterMap.map(([, value]) => `${value}`).join(" \u2022 ")}
              tooltipProps={{
                disableInteractive: true,
                title: (
                  <Stack spacing={0.5}>
                    {filterMap.map(([key, value]) => (
                      <Box key={key} sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                          {key}:
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 11 }}>
                          {value}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ),
              }}
              typographyProps={{ variant: "caption", noWrap: true, sx: { color: "text.disabled" } }}
            />
          )}
        </>
      );
    }
    return <FieldNameCaptionPart field={field} readonlyMode={readonlyMode} />;
  }, [field, filterMap, readonlyMode]);

  useEffect(() => {
    if (!field.config.ordering) {
      onChangeSorting(Ordering.Ascending);
    }
  }, [field.config.ordering, onChangeSorting]);

  return (
    <ListItem sx={{ display: "flex", gap: ".5rem", height: "32px", borderRadius: "2px", p: "5px 0" }}>
      <Box
        sx={(theme) => ({
          display: "flex",
          flex: 1,
          alignItems: "center",
          border: `1px solid ${theme.palette.text.disabled}`,
          borderRadius: "2px",
          backgroundColor: theme.palette.background.paper,
          p: "0 5px",
          overflow: "hidden",
          justifyContent: "space-between",
          minHeight: "32px",
        })}
      >
        <Box
          sx={{ display: "grid", gridTemplateColumns: "minmax(auto, max-content) minmax(auto, max-content)", gap: 0.5 }}
        >
          {Caption}
        </Box>
        {!readonlyMode && <CloseIconButton onClick={() => onRemoveItem()} iconProps={{ color: "disabled" }} />}
      </Box>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          border: `1px solid ${theme.palette.primary.main}`,
          backgroundColor: makeTintedBackgroundFromColor(theme, theme.palette.primary.main),
          borderRadius: "2px",
          p: 0,
          "&:hover": {
            cursor: "pointer",
          },
        })}
        onClick={() =>
          onChangeSorting(field.config.ordering === Ordering.Descending ? Ordering.Ascending : Ordering.Descending)
        }
      >
        <IconButton>
          {field.config.ordering === Ordering.Descending && <SortDescIcon color="primary" />}
          {field.config.ordering !== Ordering.Descending && <SortAscIcon color="primary" />}
        </IconButton>
      </Box>
    </ListItem>
  );
};

export default SortFieldItem;
